import { useCallback } from 'react';
import { usePostHog } from '@posthog/react';

export const StoreSource = {
  onboarding: 'onboarding',
  storeList: 'store_list',
  duplicate: 'duplicate',
  autoProvisioned: 'auto_provisioned',
} as const;

export type StoreSourceValue = typeof StoreSource[keyof typeof StoreSource];

export type OnboardingCompleteAction =
  | 'share_store'
  | 'create_new'
  | 'duplicate'
  | 'create_via_airbnb'
  | 'create_via_pms'
  | 'live_demo'
  | 'weekly_qa'
  | 'example_store'
  | 'dismissed';

export type ShareMethod =
  | 'link'
  | 'embed'
  | 'message_at_booking'
  | 'message_before_check_in'
  | 'message_at_check_in'
  | 'product_link';

export type SocialProvider = 'google' | 'facebook';

interface SignupProperties {
  method: 'email' | SocialProvider;
  role: string;
  has_next_param: boolean;
  // Asked on the email signup form only.
  marketing_consent?: boolean;
}

interface WalkthroughProperties {
  store_id: number;
  total_steps: number;
}

interface WalkthroughStepProperties extends WalkthroughProperties {
  step: string;
  step_index: number;
}

interface OnboardingCompleteProperties {
  store_id: number;
  // The last step reached, not a skip/complete flag: Escape on the final step still records it.
  walkthrough_last_step: string | null;
}

interface OrderProperties {
  store_id: number;
  order_id: number;
  order_total: number;
}

// The tracking plan. Saved insights filter on these exact strings, so a rename breaks an
// insight silently rather than erroring.
export interface AnalyticsEvents {
  signup_completed: SignupProperties;
  user_signed_up: SignupProperties;
  user_logged_in: { role: string; method?: 'password' | 'magic_link' | SocialProvider };
  magic_link_requested: EmptyProperties;
  magic_link_consume_failed: { reason: 'expired_or_invalid' };
  social_login_initiated: { provider: SocialProvider };
  // `provider` is the raw backend name when it is not Google or Facebook.
  social_login_failed: { provider: string; reason: string };
  social_account_connected: { provider: SocialProvider };

  onboarding_option_clicked: { option: string };
  onboarding_video_played: EmptyProperties;
  onboarding_video_completed: EmptyProperties;

  walkthrough_intro_shown: WalkthroughProperties;
  walkthrough_started: WalkthroughProperties;
  walkthrough_step_viewed: WalkthroughStepProperties;
  walkthrough_skipped: WalkthroughStepProperties;
  walkthrough_completed: WalkthroughStepProperties;
  // The tour ended itself because a target never mounted - a bug signal, not a user choice.
  walkthrough_unavailable: WalkthroughStepProperties;
  onboarding_complete_shown: OnboardingCompleteProperties;
  onboarding_complete_action_clicked: OnboardingCompleteProperties & {
    action: OnboardingCompleteAction;
  };

  store_create_submitted: { source: StoreSourceValue };
  store_created: { source: StoreSourceValue; store_id?: number };
  store_create_failed: { source: StoreSourceValue; reason: string };
  store_edited: { store_id: number; fields_changed: string[] };
  stores_imported: { platform: string; store_count: number };
  store_shared: { store_id: number; method: ShareMethod; product_id?: number };

  checkout_started: { store_id: number; order_id: number; item_count: number };
  checkout_submitted: OrderProperties & { is_zero_amount: boolean };
  payment_completed: OrderProperties & { currency: string };
  payment_failed: OrderProperties & { currency: string };

  pricing_interval_changed: { interval: 'month' | 'year' };
  subscription_plan_selected: {
    plan_name: string;
    plan_type: string;
    interval: string;
    price: number;
  };
  subscription_managed: { current_plan: string };
  addon_selected: { addon_name: string; addon_price: number };
}

type EmptyProperties = Record<string, never>;

interface CaptureOptions {
  // PostHog's `$set_once`: person properties written only if not already present.
  setOnce?: Record<string, unknown>;
}

export function useAnalytics() {
  const posthog = usePostHog();

  return useCallback(
    <E extends keyof AnalyticsEvents>(
      event: E,
      properties: AnalyticsEvents[E],
      options?: CaptureOptions,
    ) =>
      posthog?.capture(event, {
        ...properties,
        ...(options?.setOnce ? { $set_once: options.setOnce } : {}),
      }),
    [posthog],
  );
}
